import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Scales, FileText, GitDiff, ShieldCheck, Eye, Lightning } from "@phosphor-icons/react";
import WaitlistDialog from "@/components/WaitlistDialog";
import { Link } from "react-router-dom";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function Home() {
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-bg grid-bg-fade pointer-events-none" aria-hidden />
        <div className="relative container-x pt-20 md:pt-32 pb-24 md:pb-40">
          <motion.p {...fade} className="overline">
            ContractIQ · by Cognita AI
          </motion.p>

          <motion.h1
            {...fade}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="font-serif font-light tracking-tighter leading-[0.95] mt-6 text-5xl sm:text-6xl md:text-7xl lg:text-[112px] max-w-5xl"
          >
            Contract intelligence
            <br />
            <span className="italic text-[#002147]">for the modern</span> law firm.
          </motion.h1>

          <motion.p
            {...fade}
            transition={{ duration: 0.8, delay: 0.12 }}
            className="mt-8 max-w-2xl text-lg text-muted-foreground leading-relaxed"
          >
            End-to-end contract lifecycle management — drafting, negotiation, comparison, risk assessment,
            and clause-level visibility — engineered for partners, associates, and in-house counsel.
          </motion.p>

          <motion.div {...fade} transition={{ duration: 0.8, delay: 0.18 }} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-waitlist-btn"
              onClick={() => setWaitlistOpen(true)}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#002147] transition-colors"
            >
              Join the waitlist <ArrowRight size={16} />
            </button>
            <Link
              to="/product"
              data-testid="hero-product-btn"
              className="inline-flex items-center gap-2 px-7 py-3.5 border border-foreground text-foreground text-sm font-medium hover:bg-foreground hover:text-white transition-colors"
            >
              Explore the product
            </Link>
          </motion.div>

          <motion.div {...fade} transition={{ duration: 0.8, delay: 0.28 }} className="mt-20 grid grid-cols-2 md:grid-cols-4 border-y border-border">
            {[
              { k: "10x", v: "Faster review cycles" },
              { k: "97%", v: "Risk recall on tested clauses" },
              { k: "120+", v: "Pre-trained playbooks" },
              { k: "SOC 2", v: "Type II in progress" },
            ].map((s, i) => (
              <div key={i} className={`p-6 md:p-8 ${i !== 0 ? "md:border-l border-border" : ""} ${i % 2 === 1 ? "border-l border-border md:border-l" : ""} ${i >= 2 ? "border-t md:border-t-0 border-border" : ""}`}>
                <div className="font-serif text-3xl md:text-4xl tracking-tight">{s.k}</div>
                <div className="overline mt-2 text-muted-foreground">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SOCIAL PROOF MARQUEE */}
      <section className="bg-secondary border-b border-border">
        <div className="container-x py-10 flex items-center gap-10">
          <p className="overline shrink-0 text-muted-foreground hidden md:block">Trusted by teams at</p>
          <div className="overflow-hidden flex-1">
            <div className="flex gap-16 animate-marquee whitespace-nowrap">
              {[...Array(2)].flatMap((_, k) =>
                ["Wexler & Cohen", "Marlowe Partners", "Halberd LLP", "Stratton Legal", "Greenfield Counsel", "Atlas Chambers", "Northbridge Law"].map((n, i) => (
                  <span key={`${k}-${i}`} className="font-serif text-2xl text-muted-foreground/80 italic">
                    {n}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* BENTO FEATURES */}
      <section className="container-x py-24 md:py-32">
        <div className="max-w-3xl">
          <p className="overline">The product</p>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[1.02] mt-4">
            One platform for the <span className="italic">entire</span> contract lifecycle.
          </h2>
          <p className="mt-6 text-base text-muted-foreground leading-relaxed">
            ContractIQ unifies drafting, negotiation, review, and analytics into a single intelligent workspace —
            grounded in your firm&apos;s playbooks and precedent.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-6 gap-px bg-border border border-border">
          <BentoCard className="md:col-span-4 row-span-2 min-h-[360px]" icon={<FileText size={24} weight="light" />} title="Drafting & Sending" eyebrow="01">
            Generate first drafts from your firm&apos;s precedent in minutes. Send, redline, and execute — all in one place.
          </BentoCard>
          <BentoCard className="md:col-span-2" icon={<GitDiff size={24} weight="light" />} title="Comparison" eyebrow="02">
            Diff any two versions across redlines, definitions, and obligations.
          </BentoCard>
          <BentoCard className="md:col-span-2" icon={<ShieldCheck size={24} weight="light" />} title="Risk Assessment" eyebrow="03">
            Surface deviations from your playbook with severity scoring.
          </BentoCard>
          <BentoCard className="md:col-span-3" icon={<Scales size={24} weight="light" />} title="Critical Clauses" eyebrow="04">
            Identify indemnity, limitation of liability, IP, and termination clauses with citation-grade accuracy.
          </BentoCard>
          <BentoCard className="md:col-span-3" icon={<Eye size={24} weight="light" />} title="End-to-end Visibility" eyebrow="05">
            Portfolio dashboards across matters, counterparties, and obligations — exportable to your DMS.
          </BentoCard>
        </div>
      </section>

      {/* DETAILED FEATURE ROWS */}
      <section className="border-y border-border bg-secondary/40">
        <div className="container-x py-24 md:py-32 space-y-24 md:space-y-32">
          <FeatureRow
            eyebrow="Drafting & Negotiation"
            title="From precedent to executed in a single workspace."
            body="Pull from your firm&apos;s clause library, negotiate redlines collaboratively, and execute with audit-grade traceability. ContractIQ keeps the partner in control — and removes the busywork."
            bullets={["Clause library grounded in your firm's playbook", "Redline assistant with rationale", "Native DOCX in/out, no lock-in"]}
            image="https://images.pexels.com/photos/8112110/pexels-photo-8112110.jpeg"
            align="left"
          />
          <FeatureRow
            eyebrow="Risk & Comparison"
            title="See the deviations that actually matter."
            body="Compare counterparty paper against your playbook in seconds. Severity-scored deviations point counsel to what's negotiable, what's a redline, and what's a deal-breaker."
            bullets={["Multi-version diffing across definitions & obligations", "Severity-scored deviations with citations", "Custom risk taxonomies per practice group"]}
            image="https://images.pexels.com/photos/8111869/pexels-photo-8111869.jpeg"
            align="right"
          />
          <FeatureRow
            eyebrow="Portfolio Visibility"
            title="The contract estate, finally legible."
            body="Across thousands of agreements, ContractIQ surfaces obligations, renewals, and exposure — so partners and GCs walk into every conversation prepared."
            bullets={["Obligation tracking with renewal alerts", "Counterparty & matter-level dashboards", "Export to your DMS or warehouse"]}
            image="https://images.unsplash.com/photo-1714150458873-715e134901a2"
            align="left"
          />
        </div>
      </section>

      {/* QUOTE / EDITORIAL */}
      <section className="container-x py-24 md:py-32">
        <div className="max-w-4xl">
          <p className="overline">A note from the founders</p>
          <p className="font-serif text-3xl md:text-5xl mt-6 leading-[1.15] tracking-tight">
            &ldquo;The most valuable hours of legal practice are spent on judgement, not formatting.
            <span className="italic text-muted-foreground"> ContractIQ is built to give those hours back.</span>&rdquo;
          </p>
          <p className="mt-8 overline">Cognita AI · Founding Team</p>
        </div>
      </section>

      {/* CTA */}
      <section className="container-x pb-24 md:pb-32">
        <div className="border border-border p-10 md:p-16 bg-white">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <p className="overline">Early access · Q1 design partners</p>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[1.02] mt-4">
                Bring contract intelligence
                <br /><span className="italic">to your practice.</span>
              </h2>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3">
              <button
                data-testid="cta-waitlist-btn"
                onClick={() => setWaitlistOpen(true)}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#002147] transition-colors"
              >
                Join the waitlist <ArrowRight size={16} />
              </button>
              <Link
                to="/contact"
                data-testid="cta-demo-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-foreground text-foreground text-sm font-medium hover:bg-foreground hover:text-white transition-colors"
              >
                Request a demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} />
    </div>
  );
}

function BentoCard({ children, icon, title, eyebrow, className = "" }) {
  return (
    <div className={`bg-white p-8 md:p-10 hover:bg-secondary/60 transition-colors group ${className}`}>
      <div className="flex items-center justify-between">
        <div className="text-foreground/80">{icon}</div>
        <span className="font-mono text-xs text-muted-foreground tracking-[0.2em]">{eyebrow}</span>
      </div>
      <h3 className="font-serif text-2xl md:text-3xl mt-8 tracking-tight">{title}</h3>
      <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-md">{children}</p>
      <div className="mt-6 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-foreground/60 group-hover:text-foreground transition-colors">
        <Lightning size={14} weight="fill" /> Built into ContractIQ
      </div>
    </div>
  );
}

function FeatureRow({ eyebrow, title, body, bullets, image, align = "left" }) {
  const reversed = align === "right";
  return (
    <motion.div {...fade} className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center`}>
      <div className={`md:col-span-6 ${reversed ? "md:order-2" : ""}`}>
        <p className="overline">{eyebrow}</p>
        <h3 className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.05] mt-4">{title}</h3>
        <p className="mt-5 text-muted-foreground leading-relaxed">{body}</p>
        <ul className="mt-6 space-y-3">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-3 text-sm">
              <span className="font-mono text-xs text-[#002147] mt-1">0{i + 1}</span>
              <span className="text-foreground/85">{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={`md:col-span-6 ${reversed ? "md:order-1" : ""}`}>
        <div className="border border-border bg-white">
          <img src={image} alt="" className="w-full h-72 md:h-96 object-cover grayscale" loading="lazy" />
        </div>
      </div>
    </motion.div>
  );
}
